/**
 * Squad invite links, per competition (specs/competitions-hub-client Slice 3E).
 *
 * World Cup links keep their existing `/wc-invite/<code>` shape byte-for-byte —
 * they are printed on B2B collateral and shared in chats, so they must not change.
 * Other competitions use `/invite/<competitionId>/<code>`: the invite code alone
 * cannot identify a competition (codes are only unique per competition), so the
 * link carries it.
 */

import { Platform } from 'react-native';
import { PRODUCTION_APP_ORIGIN } from '../../../constants/env';
import { WORLD_CUP_COMPETITION_ID } from '../../worldcup/worldcup.constants';
import { competitionBasePath, competitionGroupRoute } from './competitionRoutes.helper';

/**
 * Two origin strategies exist in the codebase today and produce different strings
 * on native (one strips a trailing slash from PRODUCTION_APP_ORIGIN, the other
 * doesn't). Both are preserved rather than silently "fixed" here.
 */
export type InviteOriginStyle = 'worldcup' | 'b2b';

export function competitionAppOrigin(style: InviteOriginStyle = 'worldcup'): string {
  if (style === 'b2b') {
    return typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : PRODUCTION_APP_ORIGIN.replace(/\/$/, '');
  }
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return PRODUCTION_APP_ORIGIN;
}

/** Public invite path (no origin): WC keeps /wc-invite/<code>. */
export function competitionInvitePath(competitionId: string, inviteCode: string): string {
  const code = encodeURIComponent(String(inviteCode).trim());
  if (competitionId === WORLD_CUP_COMPETITION_ID) return `/wc-invite/${code}`;
  return `/invite/${encodeURIComponent(competitionId)}/${code}`;
}

/** Public squad invite URL (branded waiting-room landing). */
export function buildCompetitionSquadJoinUrl(
  competitionId: string,
  inviteCode: string,
  originStyle: InviteOriginStyle = 'worldcup',
): string {
  return `${competitionAppOrigin(originStyle)}${competitionInvitePath(competitionId, inviteCode)}`;
}

/** Squad dashboard URL when there is no invite code. */
export function buildCompetitionSquadDashboardUrl(
  competitionId: string,
  groupId: string,
  originStyle: InviteOriginStyle = 'worldcup',
): string {
  const path = competitionGroupRoute(competitionBasePath(competitionId), groupId);
  return `${competitionAppOrigin(originStyle)}${path}`;
}
